import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageHeader, Tabs } from "@/components/Kit";
import DateInput from "@/components/DateInput";
import { classRank } from "@/lib/classes";
import MonthlyAttendance from "./MonthlyAttendance";
import HolidaysTab from "./HolidaysTab";
import { holidayReason, monthBounds } from "@/lib/holidays";
import { Check, X, Save, CalendarCheck, CalendarRange, Clock, Palmtree } from "lucide-react";

const OPTS = [
  { key: "present", label: "Present", icon: Check, on: "bg-emerald-500 text-white" },
  { key: "half", label: "Half Day", icon: Clock, on: "bg-amber-500 text-white" },
  { key: "absent", label: "Absent", icon: X, on: "bg-rose-500 text-white" },
];

export default function Attendance() {
  const [tab, setTab] = useState("daily");
  const { data: students = [] } = useQuery({ queryKey: ["students"], queryFn: async () => (await api.get("/school/students")).data });
  const classes = useMemo(() => {
    const seen = [...new Set(students.map((s) => `${s.class_name}-${s.section}`))];
    return seen.sort((a, b) => {
      const [ca, sa] = a.split("-"), [cb, sb] = b.split("-");
      return classRank(ca) - classRank(cb) || String(sa).localeCompare(String(sb));
    });
  }, [students]);
  const [cls, setCls] = useState("");
  useEffect(() => { if (!cls && classes.length) setCls(classes[0]); }, [classes, cls]);

  return (
    <div data-testid="attendance-page">
      <PageHeader title="Attendance" subtitle="Daily marking with WhatsApp absentee alerts · month-wise register" />
      <Tabs testid="att-tabs" active={tab} onChange={setTab} tabs={[
        { key: "daily", label: "Daily Marking", icon: CalendarCheck },
        { key: "monthly", label: "Monthly Summary", icon: CalendarRange },
        { key: "holidays", label: "Holidays", icon: Palmtree },
      ]} />
      {tab === "daily" && <Daily students={students} classes={classes} cls={cls} setCls={setCls} />}
      {tab === "monthly" && <MonthlyAttendance classes={classes} cls={cls} setCls={setCls} />}
      {tab === "holidays" && <HolidaysTab />}
    </div>
  );
}

function Daily({ students, classes, cls, setCls }) {
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [status, setStatus] = useState({});
  const [override, setOverride] = useState(false);
  const roster = students.filter((s) => `${s.class_name}-${s.section}` === cls);

  const [mStart, mEnd] = monthBounds(date);
  const { data: hol } = useQuery({
    queryKey: ["holidays", mStart, mEnd],
    queryFn: async () => (await api.get("/school/holidays", { params: { start: mStart, end: mEnd } })).data,
  });
  const customMap = useMemo(() => Object.fromEntries((hol?.holidays || []).map((h) => [h.date, h.name])), [hol]);
  const reason = holidayReason(date, customMap, hol?.rules);
  useEffect(() => { setOverride(false); }, [date]);

  useEffect(() => {
    if (!cls) return;
    const [class_name, section] = cls.split("-");
    api.get("/school/attendance", { params: { date, class_name, section } }).then(({ data }) => {
      const init = {};
      roster.forEach((s) => (init[s.id] = "present"));
      (data.records || []).forEach((r) => { if (init[r.student_id] !== undefined) init[r.student_id] = r.status; });
      setStatus(init);
    });
    // eslint-disable-next-line
  }, [cls, date, students.length]);

  const save = useMutation({
    mutationFn: async () => {
      const [class_name, section] = cls.split("-");
      const records = roster.map((s) => ({ student_id: s.id, status: status[s.id] || "present" }));
      return (await api.post("/school/attendance", { class_name, section, date, records, override_holiday: override })).data;
    },
    onSuccess: (r) => { toast.success(`Attendance saved · ${r.absent_notified} absentee alerts sent`); qc.invalidateQueries({ queryKey: ["school-dashboard"] }); qc.invalidateQueries({ queryKey: ["att-monthly"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const present = roster.reduce((a, s) => a + (status[s.id] === "absent" ? 0 : status[s.id] === "half" ? 0.5 : 1), 0);

  return (
    <>
      <div className="ep-card mb-4 flex flex-wrap items-end gap-4 p-4">
        <label className="block"><span className="mb-1.5 block text-xs font-medium text-slate-400">Date</span><DateInput className="ep-input" value={date} onChange={(e) => setDate(e.target.value)} data-testid="att-date" /></label>
        <label className="block"><span className="mb-1.5 block text-xs font-medium text-slate-400">Class</span><select className="ep-input" value={cls} onChange={(e) => setCls(e.target.value)} data-testid="att-class">{classes.map((c) => <option key={c}>{c}</option>)}</select></label>
        <div className="ml-auto text-right"><p className="text-xs text-slate-400">Present / Total</p><p className="text-lg font-bold font-head text-emerald-400">{present} / {roster.length}</p></div>
      </div>

      {reason && (
        <div className="ep-card mb-4 border-rose-500/40 bg-rose-500/5 p-4" data-testid="att-holiday-banner">
          <p className="flex items-center gap-2 text-sm font-medium text-rose-300">
            <Palmtree size={16} /> {new Date(date + "T00:00:00").toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "short" })} is a holiday — {reason}
          </p>
          <label className="mt-2 flex cursor-pointer items-center gap-2 text-xs text-slate-400">
            <input type="checkbox" className="h-3.5 w-3.5 accent-rose-500" checked={override} onChange={(e) => setOverride(e.target.checked)} data-testid="att-override" />
            Mark anyway (treat as a working day)
          </label>
        </div>
      )}

      <div className="ep-card overflow-hidden">
        {roster.length ? roster.map((s) => {
          const st = status[s.id] || "present";
          return (
            <div key={s.id} className="flex items-center justify-between border-b border-slate-800/50 px-4 py-3" data-testid={`att-row-${s.admission_no}`}>
              <div><p className="text-sm font-medium text-white">{s.name}</p><p className="font-mono text-xs text-slate-500">{s.admission_no}</p></div>
              <div className="flex gap-2">
                {OPTS.map((o) => (
                  <button key={o.key} onClick={() => setStatus({ ...status, [s.id]: o.key })} data-testid={`${o.key}-${s.admission_no}`}
                    className={`flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium transition ${st === o.key ? o.on : "bg-slate-800 text-slate-400"}`}>
                    <o.icon size={14} /> <span className="hidden sm:inline">{o.label}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        }) : <p className="px-4 py-8 text-center text-slate-500">No students in this class</p>}
      </div>
      {roster.length > 0 && (
        <button className="ep-btn mt-4" disabled={save.isPending || (reason && !override)} onClick={() => save.mutate()} data-testid="save-attendance-btn">
          <Save size={16} /> {save.isPending ? "Saving…" : reason && !override ? "Holiday — marking disabled" : "Save Attendance"}
        </button>
      )}
    </>
  );
}
